/**
 * Super Admin → Legal Policies editor.
 *
 * Two large Markdown textareas (T&C + Privacy). "Reset to template"
 * button below each pulls the canonical Swiggy-style template from the
 * backend. "Bump version" forces every user to re-accept consent on
 * their next login (use for material policy changes).
 *
 * Endpoints:
 *   GET   /api/super/policies
 *   PATCH /api/super/policies  body: { terms_md, privacy_md, bump_version }
 */
import { useEffect, useState } from "react";
import { FileText, RefreshCw, AlertTriangle, CheckCircle2, ShieldCheck, Loader2, Eye } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function SuperPolicies() {
    const [doc, setDoc] = useState(null);
    const [terms, setTerms] = useState("");
    const [privacy, setPrivacy] = useState("");
    const [preview, setPreview] = useState(null);    // "terms" | "privacy" | null
    const [saving, setSaving] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get("/super/policies");
            setDoc(data);
            setTerms(data.terms_md || "");
            setPrivacy(data.privacy_md || "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        }
    };
    useEffect(() => { reload(); }, []);

    const save = async ({ bump = false } = {}) => {
        if (bump && !window.confirm(
            "Bump the policy version? Every user will be prompted to re-accept "
            + "Terms & Privacy on their next login. Use ONLY for material legal "
            + "changes (not typo fixes).",
        )) return;
        setSaving(true);
        try {
            const { data } = await api.patch("/super/policies", {
                terms_md: terms,
                privacy_md: privacy,
                bump_version: bump,
            });
            setDoc(data);
            toast.success(bump ? `Saved and bumped to v${data.version}` : "Saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const resetTerms = () => {
        if (!doc) return;
        if (!window.confirm("Replace the current Terms with the Swiggy-style template? Your edits will be lost.")) return;
        setTerms(doc.default_terms_md);
        toast.info("Template loaded — click Save to persist");
    };
    const resetPrivacy = () => {
        if (!doc) return;
        if (!window.confirm("Replace the current Privacy Policy with the standard template? Your edits will be lost.")) return;
        setPrivacy(doc.default_privacy_md);
        toast.info("Template loaded — click Save to persist");
    };

    if (!doc) return (
        <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>
    );

    return (
        <div data-testid="super-policies-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <ShieldCheck size={22} /> Legal Policies
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Platform-wide defaults for Terms &amp; Conditions and Privacy Policy.
                        Tenant admins can override these for their own tenant. Customers see
                        the effective policy at <code className="text-xs">/legal/terms</code> &
                        <code className="text-xs"> /legal/privacy</code>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider bg-muted rounded-full px-3 py-1.5">
                        Version {doc.version}
                    </span>
                    <button
                        data-testid="sp-bump-version"
                        onClick={() => save({ bump: true })}
                        disabled={saving}
                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-300 rounded-xl px-3 py-2 disabled:opacity-60"
                    ><AlertTriangle size={13} /> Bump version</button>
                </div>
            </div>

            <div className="mt-6 space-y-8">
                <PolicyEditor
                    testidPrefix="sp-terms"
                    title="Terms & Conditions"
                    icon={FileText}
                    md={terms}
                    setMd={setTerms}
                    onReset={resetTerms}
                    onPreview={() => setPreview("terms")}
                />
                <PolicyEditor
                    testidPrefix="sp-privacy"
                    title="Privacy Policy"
                    icon={ShieldCheck}
                    md={privacy}
                    setMd={setPrivacy}
                    onReset={resetPrivacy}
                    onPreview={() => setPreview("privacy")}
                />
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 sticky bottom-4 bg-background/80 backdrop-blur rounded-2xl border border-border p-3">
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <CheckCircle2 size={12} /> Placeholders supported:
                    <code className="bg-muted px-1.5 py-0.5 rounded text-[10px]">{"{company_name} {support_email} {support_phone} {company_address} {jurisdiction} {last_updated}"}</code>
                </span>
                <button
                    data-testid="sp-save"
                    onClick={() => save()}
                    disabled={saving}
                    className="ml-auto inline-flex items-center gap-1.5 text-sm font-bold bg-primary text-primary-foreground rounded-xl px-4 py-2 disabled:opacity-60"
                >{saving && <Loader2 size={13} className="animate-spin" />}Save</button>
            </div>

            {preview && (
                <PreviewModal
                    md={preview === "terms" ? terms : privacy}
                    title={preview === "terms" ? "Terms preview" : "Privacy preview"}
                    onClose={() => setPreview(null)}
                />
            )}
        </div>
    );
}

function PolicyEditor({ testidPrefix, title, icon: Icon, md, setMd, onReset, onPreview }) {
    return (
        <section data-testid={`${testidPrefix}-editor`} className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold inline-flex items-center gap-2"><Icon size={18} /> {title}</h2>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`${testidPrefix}-preview`}
                        onClick={onPreview}
                        className="inline-flex items-center gap-1 text-xs font-bold bg-muted rounded-xl px-2.5 py-1.5"
                    ><Eye size={12} /> Preview</button>
                    <button
                        data-testid={`${testidPrefix}-reset`}
                        onClick={onReset}
                        className="inline-flex items-center gap-1 text-xs font-bold bg-muted rounded-xl px-2.5 py-1.5"
                    ><RefreshCw size={12} /> Reset to template</button>
                </div>
            </div>
            <textarea
                data-testid={`${testidPrefix}-textarea`}
                value={md}
                onChange={(e) => setMd(e.target.value)}
                rows={18}
                spellCheck={false}
                className="w-full bg-muted/40 border border-border rounded-xl p-3 font-mono text-xs leading-relaxed outline-none focus:border-primary"
                placeholder="Paste Markdown here. Leave blank to fall back to the hardcoded Swiggy-style template."
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">
                {md.length.toLocaleString()} characters
            </p>
        </section>
    );
}

function PreviewModal({ md, title, onClose }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="sp-preview-modal">
            <div className="bg-background rounded-3xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <header className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <h3 className="font-bold">{title}</h3>
                    <button data-testid="sp-preview-close" onClick={onClose} className="text-sm font-semibold px-2 py-1 hover:bg-muted rounded">Close</button>
                </header>
                <article className="overflow-y-auto px-5 py-4 text-sm
                    [&_h1]:text-xl [&_h1]:font-extrabold [&_h1]:mb-3
                    [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2
                    [&_h3]:text-sm [&_h3]:font-bold [&_h3]:mt-3
                    [&_p]:my-2 [&_p]:text-muted-foreground
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_ul]:text-muted-foreground
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                    [&_li]:my-0.5
                    [&_strong]:text-foreground [&_strong]:font-semibold
                    [&_em]:text-xs [&_em]:block [&_em]:mb-3
                    [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:italic">
                    <ReactMarkdown>{md}</ReactMarkdown>
                </article>
            </div>
        </div>
    );
}
